import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '@/lib/ui/utils';

export type StatusPillTone = 'success' | 'destructive' | 'prize' | 'brand';
export type StatusPillSize = 'xs' | 'sm' | 'md';

export interface StatusPillProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  tone: StatusPillTone;
  /**
   * Size variant.
   * - `'md'` (default) — padding + text-body match `OutcomePill size="md"` without-image.
   * - `'sm'` — padding + text-caption match `OutcomePill size="sm"`. Used by thesis cards.
   * - `'xs'` — tighter vertical padding for dense rows (the compact activity feed).
   */
  size?: StatusPillSize;
  children: ReactNode;
  className?: string | undefined;
}

const TONE_CLASS: Record<StatusPillTone, string> = {
  success: 'bg-feedback-success-surface text-feedback-success-content',
  prize: 'bg-feedback-warning-surface text-feedback-warning-content',
  brand: 'bg-background-brand text-foreground-brand',
  destructive: 'bg-feedback-error-surface text-feedback-error-content',
};

// Padding + text size aligned with OutcomePill's per-size scale so a
// StatusPill can sit in a row alongside an OutcomePill of the same size
// without bleeding the row height.
//   - `md` matches OutcomePill `size="md"` without-image: `px-3 py-[5px] text-body`
//   - `sm` matches OutcomePill `size="sm"`:                `px-2 py-1.5  text-caption`
const SIZE_CLASS: Record<StatusPillSize, string> = {
  xs: 'gap-1 px-2 py-0.5 text-caption',
  sm: 'gap-1 px-2 py-1.5 text-caption',
  md: 'gap-1.5 px-3 py-[5px] text-body',
};

const BASE_CLASS = 'inline-flex w-fit shrink-0 items-center rounded-pill whitespace-nowrap';

export function StatusPill({
  tone,
  size = 'md',
  children,
  className,
  ...props
}: StatusPillProps): React.ReactNode {
  return (
    <span
      data-slot="status-pill"
      data-tone={tone}
      data-size={size}
      className={cn(BASE_CLASS, SIZE_CLASS[size], TONE_CLASS[tone], className)}
      {...props}
    >
      {children}
    </span>
  );
}
